import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GameProps, GameDef, playAreaStyle } from './types';

// ── STAR POP ─────────────────────────────────────────────────────────────────
// Stars/balloons pop up at random spots; tap them before they vanish. 15s.

const GAME_SECONDS = 15;
const TARGET_LIFETIME = 1800; // ms a target stays before vanishing
const SPAWN_INTERVAL = 700; // ms between spawns
const POP_EMOJIS = ['⭐', '🌟', '🎈', '✨', '💫', '🎁', '🍭', '🏆'];
const POP_BURST = ['💥', '🎉', '⭐', '✨'];

interface Target {
  id: number;
  emoji: string;
  x: number;
  y: number;
  born: number;
}

interface Pop {
  id: number;
  emoji: string;
  x: number;
  y: number;
}

function StarPop({ theme, isKid, onEnd }: GameProps) {
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [score, setScore] = useState(0);
  const [targets, setTargets] = useState<Target[]>([]);
  const [pops, setPops] = useState<Pop[]>([]);
  const nextId = useRef(0);
  const scoreRef = useRef(0);

  useEffect(() => {
    if (timeLeft <= 0) {
      onEnd({ score: scoreRef.current, noun: scoreRef.current === 1 ? 'star' : 'stars' });
      return;
    }
    const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, onEnd]);

  useEffect(() => {
    const spawn = setInterval(() => {
      const id = nextId.current++;
      setTargets(prev => [
        ...prev,
        {
          id,
          emoji: POP_EMOJIS[Math.floor(Math.random() * POP_EMOJIS.length)],
          x: 8 + Math.random() * 84,
          y: 12 + Math.random() * 76,
          born: Date.now(),
        },
      ]);
    }, SPAWN_INTERVAL);

    const expire = setInterval(() => {
      const now = Date.now();
      setTargets(prev => prev.filter(t => now - t.born < TARGET_LIFETIME));
    }, 250);

    return () => {
      clearInterval(spawn);
      clearInterval(expire);
    };
  }, []);

  const popTarget = useCallback((target: Target) => {
    setTargets(prev => prev.filter(t => t.id !== target.id));
    scoreRef.current += 1;
    setScore(scoreRef.current);
    const popId = nextId.current++;
    setPops(prev => [
      ...prev,
      { id: popId, emoji: POP_BURST[Math.floor(Math.random() * POP_BURST.length)], x: target.x, y: target.y },
    ]);
    setTimeout(() => setPops(prev => prev.filter(p => p.id !== popId)), 700);
  }, []);

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontWeight: 800, fontSize: isKid ? '1.3rem' : '1.1rem',
      }}>
        <span>⭐ {score}</span>
        <span style={{ color: timeLeft <= 5 ? '#ef4444' : theme.accent }}>⏱️ {timeLeft}s</span>
      </div>

      <div style={playAreaStyle(theme)}>
        {targets.map(t => (
          <button
            key={t.id}
            onClick={() => popTarget(t)}
            aria-label="Pop the star"
            style={{
              position: 'absolute',
              left: `${t.x}%`,
              top: `${t.y}%`,
              transform: 'translate(-50%, -50%)',
              fontSize: isKid ? '3rem' : '2.4rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              lineHeight: 1,
              animation: 'reward-pop-in 0.25s ease-out',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t.emoji}
          </button>
        ))}
        {pops.map(p => (
          <span
            key={p.id}
            style={{
              position: 'absolute',
              left: `${p.x}%`,
              top: `${p.y}%`,
              transform: 'translate(-50%, -50%)',
              fontSize: '2.6rem',
              pointerEvents: 'none',
              animation: 'reward-burst 0.6s ease-out forwards',
            }}
          >
            {p.emoji}
          </span>
        ))}
      </div>
    </>
  );
}

export const STAR_POP: GameDef = {
  id: 'star-pop',
  title: 'Star Pop',
  emoji: '⭐',
  instructions: 'Tap as many ⭐ as you can before time runs out!',
  verb: 'Pop',
  Component: StarPop,
};

export default StarPop;
